// Scroll-reveal animations for the custom sections.
(() => {
  const rootStyle = getComputedStyle(document.documentElement);
  const animationsEnabledSetting = rootStyle.getPropertyValue('--animations-enabled').trim() !== '0';
  const glitchEffectEnabled = rootStyle.getPropertyValue('--glitch-effect-enabled').trim() !== '0';
  const typewriterSpeedMultiplier = parseFloat(rootStyle.getPropertyValue('--typewriter-speed-multiplier')) || 1;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches || !animationsEnabledSetting;
  const revealSupported = !prefersReducedMotion && 'IntersectionObserver' in window;

  const LABEL_SELECTOR = '.custom-section__label, .custom-photo-header__label, .custom-photo-header__link-text, .custom-next-tour-date__label';
  const REVEAL_SELECTOR = LABEL_SELECTOR;
  const PARALLAX_SELECTOR = '.custom-section__background';

  const TYPE_DELAY_MIN = Math.round(30 * typewriterSpeedMultiplier);
  const TYPE_DELAY_MAX = Math.round(60 * typewriterSpeedMultiplier);
  const UNTYPE_DELAY_MS = Math.round(20 * typewriterSpeedMultiplier);
  const LOOP_HOLD_MIN_MS = 4000;
  const LOOP_HOLD_MAX_MS = 5000;
  const LOOP_EMPTY_PAUSE_MS = 500;
  const GLITCH_DURATION_MS = 180;
  const TYPING_COMPLETE_EVENT = 'brace:typing-complete';

  const HERO_SELECTOR = '.custom-hero';
  const HERO_INTRO_FALLBACK_MS = 2500;
  const HERO_STAGGER_MS = 150;

  const PHOTO_HEADER_SELECTOR = '.custom-photo-header';
  const NEWSLETTER_SELECTOR = '.custom-newsletter';

  const BG_PARALLAX_MAX = 18;
  const BG_PARALLAX_EASE = 0.08;
  const BG_PARALLAX_MIN_WIDTH = 750;

  const SCROLL_PARALLAX_SELECTOR = `${HERO_SELECTOR}, ${PHOTO_HEADER_SELECTOR}`;
  const SCROLL_PARALLAX_RATIO = 0.5; // background travels at ~50% of scroll speed
  const SCROLL_PARALLAX_OVERSCAN_RATIO = 0.12;

  const MUSIC_FEATURE_SELECTOR = '.custom-music-feature';
  const FEATURED_VIDEOS_ROW_SELECTOR = '.custom-featured-videos__row';
  const SLIDE_ON_SCROLL_DEAD_ZONE_RATIO = 0.25; // fraction of range with no movement, centered on 0
  const FEATURED_VIDEOS_DEAD_ZONE_RATIO = 0.45;

  const MERCH_SELECTOR = '.custom-featured-merch';
  const MERCH_STAGGER_MS = 100;

  const COLLECTION_HEADER_SELECTOR = '.custom-collection-header';
  const COLLECTION_GRID_CONTAINER_SELECTOR = '.product-grid-container';

  const ABOUT_BAND_SELECTOR = '.custom-about-band';

  const NUMBER_GLITCH_REPEAT_MS = 5000;

  const HEADER_SELECTOR = '.custom-header';
  const HEADER_SCROLLED_CLASS = 'custom-header--scrolled';
  const HEADER_READY_CLASS = 'custom-header--ready';
  const HEADER_SCROLL_ENTER_THRESHOLD = 60;
  const HEADER_SCROLL_EXIT_THRESHOLD = 30;

  let revealObserver = null;

  const startNumberGlitchLoop = (el) => {
    if (!glitchEffectEnabled) return;

    const pulse = () => {
      el.classList.add('is-glitching');
      window.setTimeout(() => el.classList.remove('is-glitching'), GLITCH_DURATION_MS);
    };

    pulse();
    window.setInterval(pulse, NUMBER_GLITCH_REPEAT_MS);
  };

  const EMPTY_LABEL_PLACEHOLDER = ' ';

  const typeLabel = (el) => {
    const fullText = el.textContent;
    el.setAttribute('aria-label', fullText);
    el.classList.add('is-typing');
    el.style.minHeight = `${el.getBoundingClientRect().height}px`;

    if (!fullText) {
      el.dispatchEvent(new CustomEvent(TYPING_COMPLETE_EVENT));
      return;
    }

    let hasSignaledComplete = false;
    const signalComplete = () => {
      if (hasSignaledComplete) return;
      hasSignaledComplete = true;
      el.dispatchEvent(new CustomEvent(TYPING_COMPLETE_EVENT));
    };

    const typeChar = (i) => {
      el.textContent = fullText.slice(0, i + 1);

      if (i + 1 >= fullText.length) {
        signalComplete();
        window.setTimeout(glitchThenUntype, LOOP_HOLD_MIN_MS + Math.random() * (LOOP_HOLD_MAX_MS - LOOP_HOLD_MIN_MS));
        return;
      }

      window.setTimeout(() => typeChar(i + 1), TYPE_DELAY_MIN + Math.random() * (TYPE_DELAY_MAX - TYPE_DELAY_MIN));
    };

    const untypeChar = (i) => {
      el.textContent = i > 0 ? fullText.slice(0, i) : EMPTY_LABEL_PLACEHOLDER;

      if (i <= 0) {
        window.setTimeout(() => typeChar(0), LOOP_EMPTY_PAUSE_MS);
        return;
      }

      window.setTimeout(() => untypeChar(i - 1), UNTYPE_DELAY_MS);
    };

    const glitchThenUntype = () => {
      if (!glitchEffectEnabled) {
        untypeChar(fullText.length);
        return;
      }

      el.classList.add('is-glitching');
      window.setTimeout(() => {
        el.classList.remove('is-glitching');
        untypeChar(fullText.length);
      }, GLITCH_DURATION_MS);
    };

    el.textContent = EMPTY_LABEL_PLACEHOLDER;
    typeChar(0);
  };

  const initHeroIntro = () => {
    if (!revealSupported) return;

    const hero = document.querySelector(HERO_SELECTOR);
    if (!hero) return;

    const heading = hero.querySelector('.custom-hero__heading');
    const subheading = hero.querySelector('.custom-hero__subheading');
    const button = hero.querySelector('.custom-hero__button');
    const label = hero.querySelector('.custom-section__label');
    const scrollText = hero.querySelector('.custom-hero__scroll-text');

    if (scrollText) typeLabel(scrollText);

    const revealHeroContent = () => {
      if (heading) heading.classList.add('is-inview');
      if (subheading) window.setTimeout(() => subheading.classList.add('is-inview'), HERO_STAGGER_MS);
      if (button) window.setTimeout(() => button.classList.add('is-inview'), HERO_STAGGER_MS * 2);
    };

    if (!label) {
      revealHeroContent();
      return;
    }

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      revealHeroContent();
    };

    label.addEventListener(TYPING_COMPLETE_EVENT, finish, { once: true });
    window.setTimeout(finish, HERO_INTRO_FALLBACK_MS);
  };

  const initBgParallax = (sectionSelector) => {
    if (prefersReducedMotion) return;
    if (!window.matchMedia(`(min-width: ${BG_PARALLAX_MIN_WIDTH}px)`).matches) return;

    const section = document.querySelector(sectionSelector);
    if (!section) return;

    const bgImg = section.querySelector('.custom-section__background img');
    if (!bgImg) return;

    const target = { x: 0, y: 0 };
    const current = { x: 0, y: 0 };

    const update = () => {
      current.x += (target.x - current.x) * BG_PARALLAX_EASE;
      current.y += (target.y - current.y) * BG_PARALLAX_EASE;
      bgImg.style.transform = `translate3d(${current.x.toFixed(2)}px, ${current.y.toFixed(2)}px, 0) scale(1.1)`;
      window.requestAnimationFrame(update);
    };

    section.addEventListener('mousemove', (event) => {
      const rect = section.getBoundingClientRect();
      const relX = (event.clientX - rect.left) / rect.width - 0.5;
      const relY = (event.clientY - rect.top) / rect.height - 0.5;
      target.x = -relX * 2 * BG_PARALLAX_MAX;
      target.y = -relY * 2 * BG_PARALLAX_MAX;
    });

    section.addEventListener('mouseleave', () => {
      target.x = 0;
      target.y = 0;
    });

    window.requestAnimationFrame(update);
  };

  const initScrollParallaxBg = () => {
    if (prefersReducedMotion) return;
    if (!('IntersectionObserver' in window)) return;

    const instances = Array.from(document.querySelectorAll(SCROLL_PARALLAX_SELECTOR))
      .map((section) => {
        const bg = section.querySelector('.custom-parallax-bg');
        return bg ? { section, bg, inView: false } : null;
      })
      .filter(Boolean);
    if (!instances.length) return;

    let ticking = false;

    const update = () => {
      ticking = false;
      const viewportCenter = window.innerHeight / 2;

      instances.forEach(({ section, bg, inView }) => {
        if (!inView) return;

        const rect = section.getBoundingClientRect();
        const sectionCenter = rect.top + rect.height / 2;
        const maxOffset = rect.height * SCROLL_PARALLAX_OVERSCAN_RATIO;
        const offset = Math.max(
          -maxOffset,
          Math.min(maxOffset, (sectionCenter - viewportCenter) * SCROLL_PARALLAX_RATIO)
        );

        bg.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
      });
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const instance = instances.find(({ section }) => section === entry.target);
          if (instance) instance.inView = entry.isIntersecting;
        });
        onScroll();
      },
      { rootMargin: '20% 0px 20% 0px' }
    );

    instances.forEach(({ section }) => observer.observe(section));
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  };

  const computeScrollProgress = (rect, viewportHeight, deadZoneRatio) => {
    const sectionCenter = rect.top + rect.height / 2;
    const viewportCenter = viewportHeight / 2;
    const range = (viewportHeight + rect.height) / 2 || 1;
    const deadZone = range * deadZoneRatio;
    const distance = Math.max(0, Math.abs(sectionCenter - viewportCenter) - deadZone);
    return Math.min(1, distance / (range - deadZone));
  };

  const initSlideOnScroll = (sectionSelector, deadZoneRatio, elements) => {
    if (prefersReducedMotion) return;

    const instances = Array.from(document.querySelectorAll(sectionSelector))
      .map((section) => {
        const targets = elements
          .map(({ selector, factor }) => ({ el: section.querySelector(selector), factor }))
          .filter((target) => target.el);
        return targets.length ? { section, targets } : null;
      })
      .filter(Boolean);
    if (!instances.length) return;

    let ticking = false;

    const update = () => {
      ticking = false;
      const viewportHeight = window.innerHeight;
      instances.forEach(({ section, targets }) => {
        const rect = section.getBoundingClientRect();
        const progress = computeScrollProgress(rect, viewportHeight, deadZoneRatio);
        targets.forEach(({ el, factor }) => {
          el.style.transform = `translateX(${(factor * 100 * progress).toFixed(2)}%)`;
        });
      });
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    update();
  };

  const initFeaturedMerchIntro = () => {
    if (!revealSupported) return;

    const grid = document.querySelector(`${MERCH_SELECTOR} .custom-featured-merch__grid`);
    if (!grid) return;

    const items = Array.from(grid.querySelectorAll('.custom-featured-merch__item'));
    if (!items.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          items.forEach((item, i) => {
            window.setTimeout(() => {
              item.classList.add('is-inview');
              const badge = item.querySelector('.custom-featured-merch__badge');
              if (badge) startNumberGlitchLoop(badge);
            }, i * MERCH_STAGGER_MS);
          });
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(grid);
  };

  const initCollectionHeaderIntro = () => {
    if (!revealSupported) return;

    const header = document.querySelector(COLLECTION_HEADER_SELECTOR);
    if (!header) return;

    const heading = header.querySelector('.custom-collection-header__heading');
    const highlights = header.querySelector('.custom-collection-header__highlights');
    const targets = [heading, highlights].filter(Boolean);
    if (!targets.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          targets.forEach((target, i) => {
            window.setTimeout(() => target.classList.add('is-inview'), i * MERCH_STAGGER_MS);
          });
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(header);
  };

  const initCollectionBadgeGlitch = () => {
    if (!revealSupported) return;

    const container = document.querySelector(COLLECTION_GRID_CONTAINER_SELECTOR);
    if (!container) return;

    const processedBadges = new WeakSet();

    const observeBadges = (grid) => {
      const badges = Array.from(grid.querySelectorAll('.custom-collection-badge')).filter(
        (badge) => !processedBadges.has(badge)
      );
      if (!badges.length) return;

      badges.forEach((badge) => processedBadges.add(badge));

      const observer = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            obs.unobserve(entry.target);
            startNumberGlitchLoop(entry.target);
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
      );

      badges.forEach((badge) => observer.observe(badge));
    };

    const grid = container.querySelector('#product-grid');
    if (grid) observeBadges(grid);

    const mutationObserver = new MutationObserver(() => {
      const freshGrid = container.querySelector('#product-grid');
      if (freshGrid) observeBadges(freshGrid);
    });
    mutationObserver.observe(container, { childList: true, subtree: true });
  };

  const initAboutBandIntro = () => {
    if (!revealSupported) return;

    const list = document.querySelector(`${ABOUT_BAND_SELECTOR} .custom-about-band__members`);
    if (!list) return;

    const numbers = Array.from(list.querySelectorAll('.custom-about-band__member-number'));
    if (!numbers.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          numbers.forEach((number) => startNumberGlitchLoop(number));
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(list);
  };

  const initBandMemberNumbersIntro = () => {
    if (!revealSupported) return;

    const list = document.querySelector('.custom-band__members');
    if (!list) return;

    const numbers = Array.from(list.querySelectorAll('.custom-band__member-number'));
    if (!numbers.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          numbers.forEach((number) => startNumberGlitchLoop(number));
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(list);
  };

  const initNewsletterBenefitsIntro = () => {
    if (!revealSupported) return;

    const list = document.querySelector('.custom-newsletter__benefits');
    if (!list) return;

    const numbers = Array.from(list.querySelectorAll('.custom-newsletter__benefit-number'));
    if (!numbers.length) return;

    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          obs.unobserve(entry.target);
          numbers.forEach((number) => startNumberGlitchLoop(number));
        });
      },
      { threshold: 0.2, rootMargin: '0px 0px -10% 0px' }
    );

    observer.observe(list);
  };

  const handleReveal = (entries, observer) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      observer.unobserve(entry.target);
      typeLabel(entry.target);
    });
  };

  const observeReveal = (root) => {
    if (!revealObserver || !root.querySelectorAll) return;
    root.querySelectorAll(REVEAL_SELECTOR).forEach((el) => {
      if (!el.classList.contains('is-typing')) {
        revealObserver.observe(el);
      }
    });
  };

  const initReveal = () => {
    if (!revealSupported) return;

    document.documentElement.classList.add('has-scroll-reveal');
    revealObserver = new IntersectionObserver(handleReveal, {
      threshold: 0.2,
      rootMargin: '0px 0px -10% 0px',
    });

    observeReveal(document);
  };

  let parallaxEls = [];
  let parallaxTicking = false;

  const updateParallax = () => {
    const viewportCenter = window.innerHeight / 2;

    parallaxEls.forEach((bg) => {
      const section = bg.closest('.custom-section');
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const sectionCenter = rect.top + rect.height / 2;
      const offset = Math.max(-24, Math.min(24, (sectionCenter - viewportCenter) * 0.06));

      bg.style.transform = `translate3d(0, ${offset.toFixed(1)}px, 0)`;
    });

    parallaxTicking = false;
  };

  const onScroll = () => {
    if (parallaxTicking) return;
    parallaxTicking = true;
    window.requestAnimationFrame(updateParallax);
  };

  const isExcludedFromSitewideParallax = (bg) => Boolean(bg.closest(SCROLL_PARALLAX_SELECTOR));

  const initParallax = () => {
    if (prefersReducedMotion) return;

    parallaxEls = Array.from(document.querySelectorAll(PARALLAX_SELECTOR)).filter(
      (bg) => !isExcludedFromSitewideParallax(bg)
    );
    if (!parallaxEls.length) return;

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    updateParallax();
  };

  const initHeaderScrollState = () => {
    const header = document.querySelector(HEADER_SELECTOR);
    if (!header) return;

    let ticking = false;

    const update = () => {
      ticking = false;
      const scrolled = header.classList.contains(HEADER_SCROLLED_CLASS);
      if (!scrolled && window.scrollY > HEADER_SCROLL_ENTER_THRESHOLD) {
        header.classList.add(HEADER_SCROLLED_CLASS);
      } else if (scrolled && window.scrollY < HEADER_SCROLL_EXIT_THRESHOLD) {
        header.classList.remove(HEADER_SCROLLED_CLASS);
      }
    };

    const onHeaderScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onHeaderScroll, { passive: true });

    update();
    window.requestAnimationFrame(() => header.classList.add(HEADER_READY_CLASS));
  };

  const init = () => {
    initHeaderScrollState();
    initReveal();
    initHeroIntro();
    initScrollParallaxBg();
    initBgParallax(HERO_SELECTOR);
    initBgParallax(PHOTO_HEADER_SELECTOR);
    initBgParallax(NEWSLETTER_SELECTOR);
    initSlideOnScroll(MUSIC_FEATURE_SELECTOR, SLIDE_ON_SCROLL_DEAD_ZONE_RATIO, [
      { selector: '.custom-music-feature__cover-art', factor: -1 },
      { selector: '.custom-music-feature__body', factor: 1 },
    ]);
    initSlideOnScroll(FEATURED_VIDEOS_ROW_SELECTOR, FEATURED_VIDEOS_DEAD_ZONE_RATIO, [
      { selector: '.custom-featured-videos__item:nth-child(1)', factor: -1 },
      { selector: '.custom-featured-videos__item:nth-child(2)', factor: 1 },
    ]);
    initFeaturedMerchIntro();
    initCollectionHeaderIntro();
    initCollectionBadgeGlitch();
    initAboutBandIntro();
    initBandMemberNumbersIntro();
    initNewsletterBenefitsIntro();
    initParallax();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: pick up sections added/reordered without a full page reload.
  document.addEventListener('shopify:section:load', (event) => {
    observeReveal(event.target);

    if (prefersReducedMotion || !event.target.querySelectorAll) return;
    event.target.querySelectorAll(PARALLAX_SELECTOR).forEach((bg) => {
      if (!parallaxEls.includes(bg) && !isExcludedFromSitewideParallax(bg)) parallaxEls.push(bg);
    });
    updateParallax();
  });

  document.addEventListener('brace:content-injected', (event) => {
    observeReveal(event.target);
  });
})();
